import './components/custom/plan-card-slider.js';
import './components/custom/plan-table.js';
import './components/custom/button.js';
import {
    EVERY_30_DAYS,
    ANNUAL,
    SDK_LOG_PREFIX,
    FEATURES_API_ENDPOINT,
    PLANS_API_ENDPOINT,
    PROCESS_PLAN_API_ENDPOINT,
    getIntervalLabel,
    safeParse,
    emitEvent
} from './helpers/helper.js';

/**
 * AppManagerBillingPage custom element
 * @property {Array} plans - List of plan objects
 * @property {Array} features - List of feature objects
 * @property {Object} currentPlan - The current plan object
 * @property {String} selectedInterval - Selected interval (monthly/annual)
 * @fires app-manager:select-plan - When a plan is selected
 */
class AppManagerBillingPage extends HTMLElement {
    static translations = {};

    static get observedAttributes() {
        return [
            'base-url',
            'shop-domain',
            'host',
            'translations',
            'discount-code'
        ];
    }

    static translateMe(key) {
        return AppManagerBillingPage.translations[key] || key;
    }

    constructor() {
        super();
        this.attachShadow({mode: 'open'});

        this._plans = [];
        this._features = [];
        this._currentPlan = {};
        this._shopifyPlan = '';
        this._selectedInterval = EVERY_30_DAYS;
        this._promotionalDiscount = null;
        this._featureValues = {};
        this._hasActiveCharge = false;
        this._discountCode = null;
        this._host = null;
        this._baseUrl = null;
        this._shopDomain = null;
        this._defaultPlanId = null;
        this._chooseLater = false;
        this._onboard = false;
        this._handleIntervalChange = this._handleIntervalChange.bind(this);
        this._handleMonthlyClick = this._handleMonthlyClick.bind(this);
        this._handleYearlyClick = this._handleYearlyClick.bind(this);
        this._handleChooseLaterClick = this._handleChooseLaterClick.bind(this);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {
            case 'base-url':
                this.baseUrl = newValue;
                break;
            case 'shop-domain':
                this.shopDomain = newValue;
                break;
            case 'host':
                this.host = newValue;
                break;
            case 'translations':
                AppManagerBillingPage.translations = safeParse(newValue, {});
                break;
            case 'discount-code':
                this.discountCode = newValue;
                break;
        }
    }

    // --- Property Getters/Setters ---
    get plans() { return this._plans; }
    set plans(value) { this._plans = value; }

    get features() { return this._features; }
    set features(value) { this._features = value; }

    get currentPlan() { return this._currentPlan; }
    set currentPlan(value) { this._currentPlan = value || {}; }

    get shopifyPlan() { return this._shopifyPlan; }
    set shopifyPlan(value) { this._shopifyPlan = value; }

    get selectedInterval() { return this._selectedInterval; }
    set selectedInterval(value) { this._selectedInterval = value; }

    get promotionalDiscount() { return this._promotionalDiscount; }
    set promotionalDiscount(value) { this._promotionalDiscount = value; }

    get featureValues() { return this._featureValues; }
    set featureValues(value) { this._featureValues = value; }

    get discountCode() { return this._discountCode; }
    set discountCode(value) { this._discountCode = value; }

    get host() { return this._host; }
    set host(value) { this._host = value; }

    get baseUrl() { return this._baseUrl; }
    set baseUrl(value) { this._baseUrl = value; }

    get shopDomain() { return this._shopDomain; }
    set shopDomain(value) { this._shopDomain = value; }

    get hasActiveCharge() { return this._hasActiveCharge; }
    set hasActiveCharge(value) { this._hasActiveCharge = !!value; }

    get defaultPlanId() { return this._defaultPlanId; }
    set defaultPlanId(value) { this._defaultPlanId = value; }

    get chooseLater() { return this._chooseLater; }
    set chooseLater(value) { this._chooseLater = !!value; }

    get onboard() { return this._onboard; }
    set onboard(value) {
        if (this._onboard !== !!value) {
            this._onboard = !!value;
            try { this._render(); } catch (e) { console.error(SDK_LOG_PREFIX, 'Error in _render (onboard setter):', e); }
        }
    }

    // --- Helper: Prepare API Params ---
    _prepareApiParams(extra = {}) {
        const params = {
            shop_domain: this.shopDomain,
            ...extra
        };
        if (this.discountCode) params['discount_code'] = this.discountCode;
        if (this.host) params['host'] = this.host;
        return params;
    }

    // --- Helper: Build Query String ---
    _buildQueryString(params) {
        return Object.entries(params)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');
    }

    async connectedCallback() {
        this.baseUrl = this.getAttribute('base-url');
        this.shopDomain = this.getAttribute('shop-domain');
        this.host = this.getAttribute('host');
        this.discountCode = this.getAttribute('discount-code');
        this._validateRequiredProps();
        await this._fetchFeatures();
        await this._fetchPlans();
        this._render();
    }

    disconnectedCallback() {
        this._removeEventListeners();
    }

    async _fetchFeatures() {
        try {
            const url = `${this.baseUrl}${FEATURES_API_ENDPOINT}`;
            const response = await fetch(url);
            if (!response.ok) {
                console.error(SDK_LOG_PREFIX, 'Failed to fetch features:', response.statusText || response.status);
                return;
            }
            const data = await response.json();
            if (!data || !Array.isArray(data.features)) return;
            this.features = data.features.filter((item) => item && item.hidden_feature !== true)
                .sort((a, b) => parseInt(a.display_order, 10) - parseInt(b.display_order, 10));
            this.features.forEach((feature) => {
                if (feature && feature.value_type === 'array') {
                    this.featureValues[feature.uuid] = feature.values;
                }
            });
        } catch (error) {
            console.error(SDK_LOG_PREFIX, 'Error in _fetchFeatures:', error);
        }
    }

    async _fetchPlans() {
        if (!this._validateRequiredProps()) return;
        try {
            const params = this._prepareApiParams();
            const url = `${this.baseUrl}${PLANS_API_ENDPOINT}?${this._buildQueryString(params)}`;
            const response = await fetch(url);
            if (!response.ok) {
                console.error(SDK_LOG_PREFIX, 'Failed to fetch plans:', response.statusText);
                return;
            }
            const data = await response.json();
            if (data && Array.isArray(data.plans) && data.plans.length) {
                this.plans = data.plans.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
                this.currentPlan = data.plan && typeof data.plan === 'object' ? data.plan : {};
                if (this.currentPlan && this.currentPlan.interval === ANNUAL) {
                    this.selectedInterval = ANNUAL;
                }
                this.shopifyPlan = typeof data.shopify_plan === 'string' ? data.shopify_plan : '';
                this.promotionalDiscount = (data.promotional_discount && typeof data.promotional_discount === 'object' && Object.keys(data.promotional_discount).length > 0) ? data.promotional_discount : {};
                this.hasActiveCharge = !!data.has_active_charge;
                this.featureValues = this.featureValues || {};
                this.defaultPlanId = data.default_plan_id || null;
                this.chooseLater = !!data.choose_later;
                this.onboard = this.defaultPlanId && this.chooseLater;
                try { this._render(); } catch (e) { console.error(SDK_LOG_PREFIX, 'Error in _render (_fetchPlans):', e); }
            }
        } catch (error) {
            console.error(SDK_LOG_PREFIX, 'Error in _fetchPlans:', error);
        }
    }

    async _fetchPlanURL(plan) {
        try {
            if (!plan || !plan.id) {
                console.error(SDK_LOG_PREFIX, 'Invalid plan object for _fetchPlanURL:', plan);
                return;
            }
            const params = this._prepareApiParams({ shop: this.shopDomain });
            const queryString = this._buildQueryString(params);
            const url = `${this.baseUrl}${PROCESS_PLAN_API_ENDPOINT}/${plan.id}?${queryString}`;
            const response = await fetch(url);
            if (!response.ok) {
                console.error(SDK_LOG_PREFIX, 'Failed to fetch plan URL:', response.statusText || response.status);
                return;
            }
            const data = await response.json();
            if (data?.plan_type === 'free_plan') {
                emitEvent(this, 'app-manager:plan-select', { free_plan: true });
            } else {
                const redirectUrl = data?.redirect_url;
                if (redirectUrl) {
                    window.top.location.href = redirectUrl;
                } else {
                    console.error(SDK_LOG_PREFIX, 'No redirect URL found in response');
                }
            }
        } catch (error) {
            console.error(SDK_LOG_PREFIX, 'Error in _fetchPlanURL:', error);
        }
    }

    _validateRequiredProps() {
        if (!this.baseUrl || !this.shopDomain) {
            console.error(SDK_LOG_PREFIX, 'base-url and shop-domain attributes are required');
            return false;
        }
        return true;
    }

    _handleIntervalChange(e) {
        this.selectedInterval = e.target.value;
        this._render();
    }

    _handleMonthlyClick() {
        this.selectedInterval = EVERY_30_DAYS;
        this._render();
    }

    _handleYearlyClick() {
        this.selectedInterval = ANNUAL;
        this._render();
    }

    async _handleChooseLaterClick() {
        try {
            const url = `${this.baseUrl}/api/app-manager/active-without-plan`;
            const payload = {
                shop_domain: this.shopDomain,
                plan_id: this.defaultPlanId
            };
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.status === true && this.onboard) {
                emitEvent(this, 'app-manager:plan-select', { choose_later: true });
                this.onboard = false;
            }
        } catch (error) {
            console.error(SDK_LOG_PREFIX, error);
        }
    }

    _render() {
        try {
            const filteredPlans = (this.plans || []).filter(p =>
                this.selectedInterval === EVERY_30_DAYS ? p.interval === EVERY_30_DAYS : p.interval === ANNUAL
            );
            // Check if there is at least one annual plan
            const hasAnnualPlan = (this.plans || []).some(p => p.interval === ANNUAL);

            this.shadowRoot.innerHTML = `
                <style>
                    :host {
                        all: initial;
                        font-family: -apple-system, BlinkMacSystemFont, San Francisco, Roboto, Segoe UI, Helvetica Neue, sans-serif;
                        font-size: 16px !important;
                    }
                    .billing-header {
                        display: flex;
                        gap: 8px;
                        max-width: calc(1200px - 8px);
                        margin: 32px auto 8px auto;
                    }
                    .choose-later-wrapper {
                        display: flex;
                        justify-content: center;
                        margin: 24px 0 0 0;
                    }
                    .app-manager__width-container,
                    .app-manager__width-container *,
                    .app-manager__width-container *::before,
                    .app-manager__width-container *::after {
                        box-sizing: border-box;
                    }
                    .app-manager__width-container {
                        padding-left: 96px;
                        padding-right: 96px;
                        max-width: 1200px;
                    }
                    .app-manager__width-container.billing-header {
                        display: flex;
                        gap: 8px;
                        margin: 32px auto 16px auto;
                    }
                    @media screen and (max-width: 1200px) {
                        .app-manager__width-container {
                            max-width: 960px;
                        }
                    }
                    @media screen and (max-width: 900px) {
                        .app-manager__width-container {
                            max-width: 760px;
                        }
                    }
                    @media screen and (max-width: 768px) {
                        .app-manager__width-container { 
                            max-width: 100%;
                            padding-left: 0;
                            padding-right: 0;
                        }
                    }
                </style>

                <div class="billing-page">
                    ${hasAnnualPlan ? `
                        <div class="billing-header app-manager__width-container" role="group" aria-label="Billing interval toggle">
                            <app-button class="billing-header__toggle billing-header__toggle--monthly" aria-label="Billed monthly" variant="${this.selectedInterval === EVERY_30_DAYS ? 'primary' : ''}" id="monthly-btn" tabindex="0">
                                Billed ${getIntervalLabel(EVERY_30_DAYS)}
                            </app-button>
                            <app-button class="billing-header__toggle billing-header__toggle--yearly" aria-label="Billed yearly" variant="${this.selectedInterval === ANNUAL ? 'primary' : ''}" id="yearly-btn" tabindex="1">
                                Billed ${getIntervalLabel(ANNUAL)}
                                <strong style="white-space: pre; display: inline-flex; gap: 3px; margin-left: 4px;">
                                    <span>17%</span>
                                    <span>Off</span>
                                </strong>
                            </app-button>  
                        </div>
                    ` : ''}

                    <plan-card-slider></plan-card-slider>
                    <plan-table></plan-table>
                    ${this.onboard ? `
                        <div class="choose-later-wrapper">
                            <a href="#" class="choose-later-anchor" style="color: inherit; text-decoration: underline; cursor: pointer;">${AppManagerBillingPage.translateMe('I will choose the plan later')}</a>
                        </div>
                    ` : ''}
                </div>
            `;

            // Set properties on child components
            const planCardSlider = this.shadowRoot.querySelector('plan-card-slider');
            if (planCardSlider) {
                planCardSlider.plans = filteredPlans;
                planCardSlider.features = this.features;
                planCardSlider.currentPlan = this.currentPlan;
                planCardSlider.promotionalDiscount = this.promotionalDiscount;
                planCardSlider.selectedInterval = this.selectedInterval;
                planCardSlider.hasActiveCharge = this.hasActiveCharge;
                planCardSlider.shopifyPlan = this.shopifyPlan;
            }
            const planTable = this.shadowRoot.querySelector('plan-table');
            if (planTable) {
                planTable.plans = filteredPlans;
                planTable.features = this.features;
                planTable.currentPlan = this.currentPlan;
                planTable.promotionalDiscount = this.promotionalDiscount;
                planTable.featureValues = this.featureValues;
                planTable.selectedInterval = this.selectedInterval;
                planTable.hasActiveCharge = this.hasActiveCharge;
                planTable.shopifyPlan = this.shopifyPlan;
            }

            this._addEventListeners();

            if (this.onboard) {
                const chooseLaterLink = this.shadowRoot.querySelector('.choose-later-wrapper a.choose-later-anchor');
                if (chooseLaterLink) {
                    chooseLaterLink.addEventListener('click', this._handleChooseLaterClick);
                }
            }
        } catch (e) {
            console.error(SDK_LOG_PREFIX, 'Error in _render:', e);
        }
    }

    // Event binding
    _addEventListeners() {
        if (!this.shadowRoot) return;
        this._removeEventListeners();
        const monthlyBtn = this.shadowRoot.querySelector('#monthly-btn');
        const yearlyBtn = this.shadowRoot.querySelector('#yearly-btn');
        if (monthlyBtn) {
            monthlyBtn.addEventListener('click', this._handleMonthlyClick);
        }
        if (yearlyBtn) {
            yearlyBtn.addEventListener('click', this._handleYearlyClick);
        }
        const planCardSlider = this.shadowRoot.querySelector('plan-card-slider');
        if (planCardSlider) {
            planCardSlider.addEventListener('app-manager:plan-click', this._handlePlanClick);
        }
        const planTable = this.shadowRoot.querySelector('plan-table');
        if (planTable) {
            planTable.addEventListener('app-manager:plan-click', this._handlePlanClick);
        }
    }

    _removeEventListeners() {
        if (!this.shadowRoot) return;
        const monthlyBtn = this.shadowRoot.querySelector('#monthly-btn');
        const yearlyBtn = this.shadowRoot.querySelector('#yearly-btn');
        if (monthlyBtn) {
            monthlyBtn.removeEventListener('click', this._handleMonthlyClick);
        }
        if (yearlyBtn) {
            yearlyBtn.removeEventListener('click', this._handleYearlyClick);
        }
        const planCardSlider = this.shadowRoot.querySelector('plan-card-slider');
        if (planCardSlider) {
            planCardSlider.removeEventListener('app-manager:plan-click', this._handlePlanClick);
        }
        const planTable = this.shadowRoot.querySelector('plan-table');
        if (planTable) {
            planTable.removeEventListener('app-manager:plan-click', this._handlePlanClick);
        }
    }

    _handlePlanClick = (e) => {
        console.log('Plan clicked:', e.detail.plan?.id);
        this._fetchPlanURL(e.detail.plan)
            .then(r => {})
            .catch(err => console.error('Error fetching plan URL:', err));
    }
}

if (!customElements.get('app-manager-billing-page')) {
    customElements.define('app-manager-billing-page', AppManagerBillingPage);
}

export {AppManagerBillingPage};